const EPS = 0.05;
export const DOOR_HEAD = 7;
export const SILL = 3;

function merge(ivs) {
  const s = [...ivs].sort((a, b) => a[0] - b[0]);
  const out = [];
  for (const [a, b] of s) {
    if (out.length && a <= out[out.length - 1][1] + EPS) out[out.length - 1][1] = Math.max(out[out.length - 1][1], b);
    else out.push([a, b]);
  }
  return out;
}

export function wallSegments(plan) {
  const lines = { h: {}, v: {} };
  const add = (o, at, iv) => { (lines[o][at] = lines[o][at] || []).push(iv); };
  for (const r of plan.rooms) {
    add("h", r.y, [r.x, r.x + r.w]); add("h", r.y + r.h, [r.x, r.x + r.w]);
    add("v", r.x, [r.y, r.y + r.h]); add("v", r.x + r.w, [r.y, r.y + r.h]);
  }
  const segs = [];
  for (const o of ["h", "v"]) {
    const limit = o === "h" ? plan.depth : plan.width;
    for (const [atS, ivs] of Object.entries(lines[o])) {
      const at = parseFloat(atS);
      for (const [s, e] of merge(ivs)) segs.push({ o, at, s, e, ext: at <= EPS || at >= limit - EPS });
    }
  }
  return segs;
}

export function wallBoxes(plan) {
  const H = plan.wall_height;
  const ops = [...plan.doors.map((d) => ({ ...d, kind: "door" })), ...plan.windows.map((w) => ({ ...w, kind: "window" }))];
  const boxes = [];
  for (const sg of wallSegments(plan)) {
    const t = sg.ext ? 0.5 : 0.33;
    const mine = [];
    for (const op of ops) {
      if (op.orientation !== sg.o) continue;
      const at = sg.o === "h" ? op.y : op.x;
      const start = sg.o === "h" ? op.x : op.y;
      if (Math.abs(at - sg.at) > EPS) continue;
      const s = Math.max(start, sg.s), e = Math.min(start + op.width, sg.e);
      if (e - s > EPS) mine.push([s, e, op.kind]);
    }
    mine.sort((a, b) => a[0] - b[0]);
    const pieces = [];
    let cur = sg.s;
    for (const [s, e, kind] of mine) {
      if (s > cur) pieces.push([cur, s, 0, H, "wall"]);
      if (kind === "window") pieces.push([s, e, 0, SILL, "wall"], [s, e, SILL, DOOR_HEAD, "glass"]);
      if (H > DOOR_HEAD) pieces.push([s, e, DOOR_HEAD, H, "wall"]);
      cur = Math.max(cur, e);
    }
    if (cur < sg.e) pieces.push([cur, sg.e, 0, H, "wall"]);
    for (const [s, e, z0, z1, mat] of pieces) {
      if (sg.o === "h") boxes.push({ x0: s, y0: sg.at - t / 2, x1: e, y1: sg.at + t / 2, z0, z1, mat });
      else boxes.push({ x0: sg.at - t / 2, y0: s, x1: sg.at + t / 2, y1: e, z0, z1, mat });
    }
  }
  return boxes;
}

export function doorArcs(plan) {
  return plan.doors.map((d) => {
    const { x, y, width: w } = d;
    if (d.orientation === "h") {
      const s = y >= plan.depth - EPS ? -1 : 1;
      return { hinge: [x, y], closed: [x + w, y], open: [x, y + s * w], w };
    }
    const s = x >= plan.width - EPS ? -1 : 1;
    return { hinge: [x, y], closed: [x, y + w], open: [x + s * w, y], w };
  });
}

export const ROOM_COLORS = {
  living: "#00F0FF", kitchen: "#FFB800", dining: "#FFB800", bedroom: "#10B981", bath: "#60A5FA",
  utility: "#A78BFA", stair: "#C084FC", circulation: "#94A3B8", storage: "#64748B", garage: "#F87171", office: "#F472B6", other: "#94A3B8",
};
export const roomColor = (t) => ROOM_COLORS[t] || ROOM_COLORS.other;
export const fmtFt = (v) => {
  const ft = Math.floor(v), inch = Math.round((v - ft) * 12);
  return inch ? `${ft}'-${inch}"` : `${ft}'`;
};

export const floorView = (plan, i) => ({ width: plan.width, depth: plan.depth, wall_height: plan.wall_height, ...plan.floors[i] });
export const levelZ = (plan, i) => i * (plan.wall_height + 1);
export const roomCount = (plan) => plan.floors.reduce((s, f) => s + f.rooms.length, 0);

export function stairBoxes(r, z0, z1) {
  const n = Math.max(8, Math.round((z1 - z0) / 0.625));
  const rise = (z1 - z0) / n, alongX = r.w >= r.h, run = (alongX ? r.w : r.h) / n;
  return Array.from({ length: n }, (_, k) => {
    const a = k * run, b = (k + 1) * run, top = z0 + (k + 1) * rise;
    return alongX
      ? { x0: r.x + a, y0: r.y, x1: r.x + b, y1: r.y + r.h, z0, z1: top, mat: "stair" }
      : { x0: r.x, y0: r.y + a, x1: r.x + r.w, y1: r.y + b, z0, z1: top, mat: "stair" };
  });
}

export function sitePlacement(plan, site) {
  const bw = site.lot_width - site.setback_left - site.setback_right;
  const bd = site.lot_depth - site.setback_front - site.setback_rear;
  return { bw, bd, hx: site.setback_left + Math.max(0, (bw - plan.width) / 2), hy: site.setback_front,
    fits: plan.width <= bw + 0.01 && plan.depth <= bd + 0.01 };
}

const MIN_ROOM = 3;
const edgeInfo = (r, seg) => {
  const v = seg.o === "v";
  return { a0: v ? r.y : r.x, a1: v ? r.y + r.h : r.x + r.w, start: v ? r.x : r.y, len: v ? r.w : r.h };
};
const onSeg = (seg, a0, a1) => a0 >= seg.s - EPS && a1 <= seg.e + EPS;

export function wallMoveRange(fl, seg) {
  let lo = -Infinity, hi = Infinity;
  for (const d of fl.doors) {
    if (d.orientation === seg.o) continue;
    const across = seg.o === "v" ? d.y : d.x, s0 = seg.o === "v" ? d.x : d.y, s1 = s0 + d.width;
    if (across < seg.s - EPS || across > seg.e + EPS) continue;
    if (s0 >= seg.at - EPS) hi = Math.min(hi, s0 - seg.at - 0.5);
    else if (s1 <= seg.at + EPS) lo = Math.max(lo, s1 - seg.at + 0.5);
  }
  for (const r of fl.rooms) {
    const { a0, a1, start, len } = edgeInfo(r, seg);
    if (!onSeg(seg, a0, a1)) continue;
    if (Math.abs(start - seg.at) < EPS) hi = Math.min(hi, len - MIN_ROOM);
    else if (Math.abs(start + len - seg.at) < EPS) lo = Math.max(lo, MIN_ROOM - len);
  }
  return [lo, hi];
}

export function applyWallMove(plan, fi, seg, d) {
  const v = seg.o === "v";
  const floors = plan.floors.map((f, i) => {
    if (i !== fi) return f;
    const rooms = f.rooms.map((r) => {
      const { a0, a1, start, len } = edgeInfo(r, seg);
      if (!onSeg(seg, a0, a1)) return r;
      const n = { ...r };
      if (Math.abs(start - seg.at) < EPS) { if (v) { n.x += d; n.w -= d; } else { n.y += d; n.h -= d; } }
      else if (Math.abs(start + len - seg.at) < EPS) { if (v) n.w += d; else n.h += d; }
      n.area = n.w * n.h;
      return n;
    });
    const mv = (o) => {
      if (o.orientation !== seg.o) return o;
      const at = v ? o.x : o.y, st = v ? o.y : o.x;
      if (Math.abs(at - seg.at) > EPS || !onSeg(seg, st, st + o.width)) return o;
      return { ...o, [v ? "x" : "y"]: at + d };
    };
    return { ...f, rooms, doors: f.doors.map(mv), windows: f.windows.map(mv), area: rooms.reduce((s, r) => s + r.area, 0) };
  });
  return { ...plan, floors, total_area: floors.reduce((s, f) => s + f.area, 0) };
}
